import { type FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import CustomerSchedulingCalendar from '@/components/Booking/CustomerSchedulingCalendar';
import TeamMemberChips from '@/components/Booking/TeamMemberChips';
import Button from '@/components/DesignLibrary/Button';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import UserImage from '@/components/UserImage';
import * as format from '@/format';
import useNoodleApi from '@/hooks/useNoodleApi';
import useUserWorkflowContext from '@/hooks/useUserWorkflowContext';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import commonStyles from '@/modals/ModalsCommon.module.scss';
import { getProductById } from '@/tsClient';
import { useTeamMembersWithAvailability } from '@/tsClient/scheduling/getTeamMembersWithAvailability';
import type * as ApiModels from '@/typings/api-models';

import s from './ScheduleBooking.module.scss';

export type ScheduleBookingProps = {
  booking: Pick<ApiModels.Booking, 'productId' | 'teamMemberPersonId'> &
    Parameters<typeof CustomerSchedulingCalendar>[0]['booking'] & {
      price: Pick<ApiModels.Price, 'id' | 'priceTitle'> & {
        product: Pick<ApiModels.Product, 'id' | 'title'>;
      };
      person: Pick<ApiModels.Person, 'id' | 'name'> & {
        primaryColour?: { hex: string } | null;
        image?: { url: string } | null;
      };
      creator: Pick<ApiModels.Creator, 'id' | 'timezone'>;
    };
  onSchedule?: () => Promise<void>;
};

const OVERRIDE_48_HOUR_CREATOR_IDS: string[] = [
  'clw9oyao3neis07lnaavr2nwf', // Eranthe Law
];

const ScheduleBookingModal: FC<ScheduleBookingProps> = ({ booking, onSchedule }) => {
  const [screen, setScreen] = useState<'Book' | 'Review'>(booking.startAt ? 'Review' : 'Book');
  const { assignedAttorneyPersonId } = useUserWorkflowContext();
  const { isInDashboard } = useContext(DashboardContext);

  const {
    data: productData,
    fetchingState: { isFetching: isFetchingProduct },
    getData: getProduct,
  } = useNoodleApi(getProductById);
  const { data: teamMembers } = useTeamMembersWithAvailability(
    productData?.product.id ? { productId: productData.product.id } : null,
  );

  useEffect(() => {
    void getProduct({ id: booking.productId });
  }, [booking, getProduct]);

  const assignedTeamMember = useMemo(
    () =>
      assignedAttorneyPersonId && teamMembers
        ? (teamMembers.find((member) => member.id === assignedAttorneyPersonId) ?? null)
        : null,
    [assignedAttorneyPersonId, teamMembers],
  );

  // Derive the default selection: attorney if they have availability, then existing booking assignment, then first team member
  const defaultTeamMemberPersonId = useMemo(() => {
    if (assignedTeamMember) return assignedTeamMember.id;
    if (booking.teamMemberPersonId) return booking.teamMemberPersonId;
    if (teamMembers && teamMembers.length > 0) return teamMembers[0].id;
    return null;
  }, [assignedTeamMember, booking.teamMemberPersonId, teamMembers]);

  // Only track user overrides — null means "use the default"
  const [userSelectedPersonId, setUserSelectedPersonId] = useState<string | null>(null);
  const selectedTeamMemberPersonId = userSelectedPersonId ?? defaultTeamMemberPersonId;

  const handleTeamMemberSelect = useCallback((personId: string | null) => {
    setUserSelectedPersonId(personId);
  }, []);

  const handleBookingScheduled = async (): Promise<void> => {
    await onSchedule?.();
  };

  const isWithin48Hours =
    !!booking.startAt && new Date(new Date(booking.startAt).getTime() - 1000 * 60 * 60 * 24 * 2) < new Date();

  const isButtonEnabled =
    isInDashboard || OVERRIDE_48_HOUR_CREATOR_IDS.includes(booking.creator.id) || !isWithin48Hours;

  return (
    <div className={commonStyles.container}>
      {screen === 'Book' && (
        <>
          {isFetchingProduct || !productData?.product ? (
            <ProgressIndicator isCentered />
          ) : (
            <div className={s.calendar}>
              {assignedTeamMember && (
                <TeamMemberChips
                  teamMembers={[assignedTeamMember]}
                  selectedTeamMemberPersonId={assignedTeamMember.id}
                  onSelect={() => {}}
                />
              )}
              {assignedAttorneyPersonId && teamMembers && !assignedTeamMember && (
                <>
                  <p className='body-sm px-4 py-2 text-muted-foreground'>
                    The attorney assigned to this case has no availability for this appointment. Reach out in chat or
                    select from another member of the team.
                  </p>
                  <TeamMemberChips
                    teamMembers={teamMembers}
                    selectedTeamMemberPersonId={selectedTeamMemberPersonId}
                    onSelect={handleTeamMemberSelect}
                  />
                </>
              )}
              {!assignedAttorneyPersonId && teamMembers && !booking.teamMemberPersonId && (
                <TeamMemberChips
                  teamMembers={teamMembers}
                  selectedTeamMemberPersonId={selectedTeamMemberPersonId}
                  onSelect={handleTeamMemberSelect}
                />
              )}
              <CustomerSchedulingCalendar
                availabilityTimezone={booking.creator.timezone}
                priceId={booking.priceId}
                product={productData?.product}
                selectedTeamMemberPersonId={selectedTeamMemberPersonId}
                booking={booking}
                onFinish={handleBookingScheduled}
                debugKey='ScheduleBookingModal'
              />
            </div>
          )}
        </>
      )}
      {screen === 'Review' && (
        <div className={s.review}>
          <div className={s.content}>
            <p className='body-lg' style={{ textAlign: 'center' }}>
              {booking.price.priceTitle || booking.price.product.title || 'Booking'}
            </p>
            <Spacer size='16px' />
            <p className='caption' style={{ color: 'var(--color-gray-75)', textAlign: 'center' }}>
              Contact
            </p>
            <Spacer size='8px' />
            <div className={s.customer}>
              <UserImage
                size={24}
                color={booking.person.primaryColour?.hex || null}
                name={booking.person.name}
                url={booking.person.image?.url}
              />
              <p className='body-sm'>{booking.person.name}</p>
            </div>
            <Spacer size='16px' />
          </div>
          {booking.startAt && (
            <div className={s.reviewCell}>
              <p className='caption'>Date & Time:</p>
              <Spacer size='16px' />
              <p className='body-sm'>
                {format.datetimeLocalTZ.withTimezone({ datetime: booking.startAt, timezone: booking.creator.timezone })}
              </p>
            </div>
          )}
          <Spacer size='16px' />
          <Button variant='primary' disabled={!isButtonEnabled} size='md' fullWidth onClick={() => setScreen('Book')}>
            {isButtonEnabled ? 'Reschedule' : 'Can not reschedule within 48 hours'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScheduleBookingModal;
